/**
 * Lane C0-PROPS: building blocks the kit's buildings share (render/kit/buildings*.ts): walls on an inset rectangle,
 * timber frames, rows of windows, deep upswept eaves with gold horn tips and tassels, plinths with key-pattern trims.
 * Every structural face goes through buildings.ts `wall` / `top` / `roof` (noted for route C rotation).
 */
import { hash2, valueNoise } from '@nine/shared';
import { eaveLift } from './curvedRoof';
import {
  ashlar,
  box,
  brick,
  flags,
  lattice,
  M,
  roof,
  snap,
  wall,
  type Pix,
  type Proj,
  type Ramp,
  type Side,
  type Tex,
} from '../buildings';
import { cornerTassels, eaveShadow, K, keyBand, ridgeEnds, roofEdges } from './parts';

export interface Stage {
  readonly px: Pix;
  readonly P: Proj;
  readonly ax: number;
  readonly ay: number;
}

export type Rect = readonly [x0: number, y0: number, x1: number, y1: number];

/** Both near faces of a box from z0 to z1 in one texture (front-left: y = y1; front-right: x = x1). */
export function faces(s: Stage, [x0, y0, x1, y1]: Rect, z0: number, z1: number, tex: Tex): void {
  wall(s.px, s.P, 'left', y1, x0, x1, z0, z1, tex);
  wall(s.px, s.P, 'right', x1, y0, y1, z0, z1, tex);
}

/** A face's span: [at, u0, u1] for a side of a rectangle. */
export const span = (r: Rect, side: Side): [number, number, number] =>
  side === 'left' ? [r[3], r[0], r[2]] : [r[2], r[1], r[3]];

/** A stone plinth with a key-pattern band along its top edge and flags on top. */
export function plinthK(s: Stage, r: Rect, h: number, band = true): void {
  box(s.px, s.P, r, 0, h, ashlar, flags);
  if (band && h >= 10) faces(s, r, h - 4, h - 1, (u, v, l) => snap(M.stone, l + 0.22 + ((u | 0) % 4 === 0 ? -0.2 : 0), u | 0, v | 0, 0));
}

/** Timber posts along a face every `bay` tiles, and horizontal beams at the given heights. */
export function timber(
  s: Stage,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  bay: number,
  beams: readonly number[],
  mat: Ramp = M.timber,
  eave = 1e9,
): void {
  for (const z of beams)
    wall(s.px, s.P, side, at, u0, u1, z - 1.5, z + 1.5, (u, v, l) =>
      snap(mat, l + (v > z + 0.5 ? 0.12 : -0.04) - (v > eave - 6 ? 0.3 : 0), u | 0, v | 0, 0),
    );
  const n = Math.max(1, Math.round((u1 - u0) / bay));
  for (let k = 0; k <= n; k++) {
    const u = u0 + ((u1 - u0) * k) / n;
    const a = Math.max(u0, u - 0.08);
    const b = Math.min(u1, u + 0.08);
    wall(s.px, s.P, side, at, a, b, z0, z1, (uu, v, l) => {
      const lit = (side === 'left' ? b * 16 - uu : uu - a * 16) < 1.2 ? 0.16 : 0;
      return snap(mat, l + lit + (valueNoise(uu, v / 8) - 0.5) * 0.18 - (v > eave - 6 ? 0.3 : 0), uu | 0, v | 0);
    });
  }
}

/** `n` lattice windows spread along a face between u0 and u1 (skipping [skip0, skip1]). */
export function windows(
  s: Stage,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  n: number,
  glow = 0.42,
  eave = 1e9,
  skip: readonly [number, number] = [0, -1],
): void {
  const w = (u1 - u0) / n;
  for (let k = 0; k < n; k++) {
    const a = u0 + w * k + w * 0.22;
    const b = u0 + w * (k + 1) - w * 0.22;
    if (b > skip[0] && a < skip[1]) continue;
    lattice(s.px, s.P, side, at, a, b, z0, z1, glow, eave);
  }
}

/** Plaster walls with a brick or stone footing on both near faces of `r`. */
export function plasterWalls(s: Stage, r: Rect, z0: number, z1: number, footing: 'brick' | 'stone' | 'none', eave = z1 + 6): void {
  const zf = footing === 'none' ? z0 : z0 + 14;
  if (footing === 'brick') faces(s, r, z0, zf, brick(eave));
  if (footing === 'stone') faces(s, r, z0, zf, ashlar);
  faces(s, r, zf, z1, kitPlaster(zf, eave));
}

/** Lime plaster on the kit's brighter ramp: mottled, damp at the foot, rain streaks (cf. buildings.ts `plaster`). */
export const kitPlaster =
  (foot: number, eave: number): Tex =>
  (u, v, l) => {
    l += 0.06 + (valueNoise(u / 9, v / 14) - 0.5) * 0.14 + (hash2(u | 0, v | 0) - 0.5) * 0.04;
    if (v - foot < 10) l -= (1 - (v - foot) / 10) * 0.25;
    if (valueNoise(u / 2.5 + 7, v / 26) > 0.8) l -= 0.1;
    return snap(K.wallPlaster, l - (v > eave - 8 ? 0.2 : 0), u | 0, v | 0);
  };

/** Board walls (stables, sheds, stilt houses). */
export function boardWalls(s: Stage, r: Rect, z0: number, z1: number, mat: Ramp = M.wood, eave = z1 + 6): void {
  faces(s, r, z0, z1, (u, v, l) => {
    const board = Math.floor(u / 5);
    if (u % 5 < 1) return snap(mat, l - 0.3 - (v > eave - 6 ? 0.25 : 0), u | 0, v | 0, 0);
    return snap(mat, l + (hash2(board, 3) - 0.5) * 0.16 + (valueNoise(u * 0.6 + board * 13, v / 7) - 0.5) * 0.2 - (v > eave - 6 ? 0.3 : 0), u | 0, v | 0);
  });
}

/** A key-pattern trim band on both faces at height z (6 px). */
export function trim(s: Stage, r: Rect, z: number, field: Ramp = K.maroon): void {
  faces(s, r, z, z + 6, keyBand(z, field));
}

/** Horn tips: the near eave corners swept up and out, gold-tipped (stronger than `roof`'s own curl). */
export function horns(s: Stage, [x0, y0, x1, y1]: Rect, ze: number, tiles: Ramp, size = 1): void {
  for (const [cx, cy, dir] of [
    [x0, y1, -1],
    [x1, y1, 0],
    [x1, y0, 1],
    [x0, y0, 0],
  ] as const) {
    const [ex, ey] = s.P(cx, cy, ze);
    const n = Math.round(8 * size);
    for (let k = 0; k < n; k++) {
      const q = k / (n - 1);
      const x = Math.round(ex + dir * q * q * 6 * size + (dir === 0 ? 0 : 0));
      const y = Math.round(ey - k * 1.1);
      const tip = k >= n - 2;
      s.px.set(x, y, tip ? M.gold[3]! : tiles[k < n / 2 ? 2 : 3]!);
      s.px.set(x + 1, y, tip ? M.gold[2]! : tiles[1]!);
      if (k < n / 2) s.px.set(x - 1, y, tiles[2]!);
    }
  }
}

export interface RoofOpts {
  /** Ridge ornaments (default true). */
  readonly ridge?: boolean;
  /** Horn tips at the eave corners (scale; 0: none). */
  readonly horns?: number;
  /** Tassels under the near corners. */
  readonly tassels?: boolean;
  /** Fascia ramp. */
  readonly edge?: Ramp;
  /** Depth of the eave's shadow on what is under it (px; 0: none). */
  readonly shadow?: number;
}

/** A hip (or pyramid) roof with the kit's dressing: horns, tassels, ridge beasts. */
export function hipRoof(s: Stage, eaves0: Rect, ze: number, top: Rect, zt: number, tiles: Ramp, o: RoofOpts = {}): void {
  // Deeper eaves (BUILDINGS-R1 §2): the overhang grows by 0.4 tile all round.
  const m = Math.min(eaves0[2] - eaves0[0], eaves0[3] - eaves0[1]) >= 2.5 ? 0.4 : 0;
  const eaves: Rect = [eaves0[0] - m, eaves0[1] - m, eaves0[2] + m, eaves0[3] + m];
  const lift = eaveLift(eaves, zt - ze);
  if (o.shadow !== 0) eaveShadow(s.px, s.P, eaves, ze, o.shadow ?? 20, lift);
  roof(s.px, s.P, eaves, ze, top, zt, tiles, o.edge ?? M.stone, o.ridge ?? true);
  roofEdges(s.px, s.P, eaves, ze, top, zt, tiles);
  if (o.horns !== 0) horns(s, eaves, ze + lift, tiles, o.horns ?? 1);
  if (o.tassels) cornerTassels(s.px, s.P, eaves, ze + lift);
  if ((o.ridge ?? true) && (top[0] !== top[2] || top[1] !== top[3]))
    ridgeEnds(s.px, s.P, [top[0], top[1], zt], [top[2], top[3], zt], tiles, 1);
}

/** A red paper lantern string along a face's eave (hangs at height z). */
export { lantern } from '../buildings';
